/**
 * Blend Discovery Platform — Ant Design v5 ConfigProvider themes.
 *
 * Exports `lightTheme` and `darkTheme` for direct use with `<ConfigProvider theme={…}>`.
 * Built on AntD's `theme.defaultAlgorithm` / `theme.darkAlgorithm` so derived tokens
 * (`colorPrimaryHover`, `colorPrimaryActive`, `colorLinkHover`, etc.) come from the
 * algorithm rather than hand-tuned values.
 *
 * Seed tokens match the Blend brand palette synced from the Figma design system
 * (file `pawpk8ZDqdWUEPJm7NwBIt`) on 2026-05-18.
 */

import { theme, type ThemeConfig } from 'antd';

// ─── Primitives ───────────────────────────────────────────────────────────────
// Named so a brand/status hex only needs to change in one place per mode-role,
// instead of at every hardcoded call site. Not exported — internal DRY only;
// `_theme-vars.scss` keeps its own separately-declared primitives (see that
// file's header comment for why the two aren't unified across languages).

const BRAND_500 = '#053057';
const BRAND_CYAN = '#A2F3F3';
const CREAM = '#f4f3f0';
const WHITE = '#ffffff';
const DARK_BG = '#0b0d0e';
const DARK_CONTAINER = '#1a1f24';
const DARK_CHROME = '#141a1e';
const SUCCESS = '#109b48';
const WARNING = '#c08a0f';
const ERROR = '#c0302d';
const INFO = '#314550';
const BODY_TEXT = '#4a4a4a';
const SECONDARY_TEXT = '#737373';
const CYAN_MUTED = '#3d8f8f';
const TABLE_SORT_LIGHT = '#0a3f6e';
const TABLE_SORT_DARK = '#8ddede';

// ─── Shared component overrides ──────────────────────────────────────────────
// Applied to both light and dark themes. Dark adds `Button.solidTextColor` on top.

const sharedComponents: NonNullable<ThemeConfig['components']> = {
  Button: {
    fontWeight: 500,
    paddingInline: 14,
    paddingInlineLG: 16,
    primaryShadow: '0 2px 0 rgba(5,48,87,0.1)',
  },
  Input: {
    activeShadow: '0 0 0 2px rgba(5,48,87,0.1)',
    errorActiveShadow: '0 0 0 2px rgba(192,48,45,0.1)',
    warningActiveShadow: '0 0 0 2px rgba(192,138,15,0.1)',
  },
};

// Light-mode form input chrome — cool-gray border and placeholder so inputs
// read as cooler/cleaner against the white app surface (the AntD default
// `#d9d9d9` border and 25% black placeholder are warm neutrals). Scoped per
// form-component so non-input surfaces are untouched.
const lightInputBorder = '#9CA3AF';
const lightInputPlaceholder = '#9CA3AF';

// ─── Light theme ─────────────────────────────────────────────────────────────

export const lightTheme: ThemeConfig = {
  algorithm: theme.defaultAlgorithm,
  token: {
    colorPrimary: BRAND_500,
    colorSuccess: SUCCESS,
    colorWarning: WARNING,
    colorError: ERROR,
    colorInfo: INFO,
    // colorTextBase remains navy so AntD derives heading/title colors from brand;
    // body paragraph and secondary text are explicitly overridden to dark grays
    // below to give content a more neutral, readable tone.
    colorTextBase: BRAND_500,
    colorText: BODY_TEXT,
    colorTextSecondary: SECONDARY_TEXT,
    // `<Typography type="secondary">` reads `colorTextDescription`, not
    // `colorTextSecondary` — keep both aligned so secondary copy renders gray.
    colorTextDescription: SECONDARY_TEXT,
    colorBgBase: WHITE,
    fontFamily: 'Montserrat, sans-serif',
    fontSize: 16,
    borderRadius: 8,
  },
  components: {
    ...sharedComponents,
    Input: {
      ...sharedComponents.Input,
      colorBorder: lightInputBorder,
      colorTextPlaceholder: lightInputPlaceholder,
    },
    InputNumber: {
      colorBorder: lightInputBorder,
      colorTextPlaceholder: lightInputPlaceholder,
    },
    Select: {
      colorBorder: lightInputBorder,
      colorTextPlaceholder: lightInputPlaceholder,
    },
    DatePicker: {
      colorBorder: lightInputBorder,
      colorTextPlaceholder: lightInputPlaceholder,
    },
    Mentions: {
      colorBorder: lightInputBorder,
      colorTextPlaceholder: lightInputPlaceholder,
    },
    Cascader: {
      colorBorder: lightInputBorder,
      colorTextPlaceholder: lightInputPlaceholder,
    },
    TreeSelect: {
      colorBorder: lightInputBorder,
      colorTextPlaceholder: lightInputPlaceholder,
    },
    Radio: {
      colorBorder: lightInputBorder,
    },
    Checkbox: {
      colorBorder: lightInputBorder,
    },
    Tooltip: {
      // Invert AntD's default dark-spotlight tooltip — off-white bg with dark
      // gray body-text color so tooltips read as light surfaces in light mode.
      colorBgSpotlight: CREAM,
      colorTextLightSolid: BODY_TEXT,
    },
    Segmented: {
      // Active segment renders as full brand navy (`--color-brand-500`) with
      // white text so the selected pill carries the same brand presence as
      // primary buttons rather than fading into the track bg. Inactive
      // segments use navy text so they clear WCAG AA on the light track bg
      // (AntD's default gray text lands well under 4.5:1).
      itemSelectedBg: BRAND_500,
      itemSelectedColor: WHITE,
      itemColor: BRAND_500,
      itemHoverColor: BRAND_500,
    },
    Menu: {
      // Active (selected) menu item — navy bg + white text. White on
      // `#053057` clears WCAG AA easily (~15:1). AntD's default pairs a pale
      // tint bg with brand-colored text and lands well under AA on the small
      // text size used in menu items.
      itemSelectedBg: BRAND_500,
      itemSelectedColor: WHITE,
    },
    Table: {
      // Header row uses full brand navy (`--color-brand-500`) with white text
      // so it reads as a branded band against the white app surface. Sort
      // active/hover bgs lift one step (`#0a3f6e`) instead of darkening —
      // there's no room below navy to keep the affordance visible.
      headerBg: BRAND_500,
      headerColor: WHITE,
      headerSortActiveBg: TABLE_SORT_LIGHT,
      headerSortHoverBg: TABLE_SORT_LIGHT,
      fixedHeaderSortActiveBg: TABLE_SORT_LIGHT,
      rowHoverBg: CREAM,
      // Square corners — opt out of the global 8px borderRadius for tables.
      borderRadius: 0,
      borderRadiusLG: 0,
      headerBorderRadius: 0,
    },
  },
};

// ─── Dark theme ──────────────────────────────────────────────────────────────

export const darkTheme: ThemeConfig = {
  algorithm: theme.darkAlgorithm,
  token: {
    // Seed tokens
    colorPrimary: BRAND_CYAN,
    colorSuccess: SUCCESS,
    colorWarning: WARNING,
    colorError: ERROR,
    colorInfo: INFO,
    colorTextBase: CREAM,
    colorBgBase: DARK_BG,
    fontFamily: 'Montserrat, sans-serif',
    fontSize: 16,
    borderRadius: 8,

    // Map overrides — bypass darkAlgorithm derivations for these specific tokens
    colorBgContainer: DARK_CONTAINER,
    colorBgElevated: '#242c32',
    colorText: CREAM,
    colorTextSecondary: BRAND_CYAN,
    colorTextTertiary: '#6abfbf',
    colorTextQuaternary: CYAN_MUTED,
    colorTextPlaceholder: CREAM,
    colorFill: DARK_CONTAINER,
    colorFillSecondary: DARK_CHROME,
    colorFillTertiary: '#0f1418',
    colorFillQuaternary: DARK_BG,
    colorBorder: '#615e58',
    // Text color on solid/primary buttons (and other "filled" surfaces). AntD's
    // darkAlgorithm derives this as white by default; the cyan dark primary needs
    // near-black for legible contrast on solid buttons.
    colorTextLightSolid: DARK_BG,
    // Link triplet tracks the primary chain: setting colorLink seeds it and the
    // darkAlgorithm derives colorLinkHover / colorLinkActive automatically, so
    // they end up equal to colorPrimaryHover / colorPrimaryActive (spec intent).
    colorLink: BRAND_CYAN,
  },
  components: {
    ...sharedComponents,
    Button: {
      ...sharedComponents.Button,
      // Text on the cyan primary solid button — near-black for legibility.
      solidTextColor: DARK_BG,
      // Danger buttons render on the deep-red `colorError` bg; near-black text
      // fails WCAG contrast there. Use white to clear AA contrast minimums.
      dangerColor: WHITE,
    },
    Table: {
      headerBg: BRAND_CYAN,
      headerColor: DARK_BG,
      headerSortActiveBg: TABLE_SORT_DARK,
      headerSortHoverBg: TABLE_SORT_DARK,
      fixedHeaderSortActiveBg: TABLE_SORT_DARK,
      rowHoverBg: DARK_CONTAINER,
      // Square corners — opt out of the global 8px borderRadius for tables.
      borderRadius: 0,
      borderRadiusLG: 0,
      headerBorderRadius: 0,
    },
    Menu: {
      darkItemBg: DARK_CHROME,
      darkPopupBg: DARK_CHROME,
      darkSubMenuItemBg: DARK_BG,
      darkItemColor: CREAM,
      darkItemHoverColor: BRAND_CYAN,
      darkGroupTitleColor: BRAND_CYAN,
      darkItemDisabledColor: CYAN_MUTED,
      // Active (selected) item — near-black text + icon on the turquoise
      // selected bg. Icons inherit `darkItemSelectedColor`.
      darkItemSelectedColor: DARK_BG,
    },
    Layout: {
      headerBg: DARK_CHROME,
      siderBg: DARK_CHROME,
      triggerBg: DARK_CONTAINER,
    },
    Tooltip: {
      // Global colorTextLightSolid is near-black so solid primary buttons stay
      // legible on cyan; tooltips render on the dark spotlight bg and need
      // off-white text instead.
      colorTextLightSolid: CREAM,
    },
    Pagination: {
      // darkAlgorithm leaves pagination item bgs near-white; pin them to the
      // dark container surface so the strip matches the rest of the table.
      itemBg: DARK_CONTAINER,
      itemLinkBg: DARK_CONTAINER,
      itemInputBg: DARK_CONTAINER,
      itemActiveBg: DARK_CONTAINER,
    },
    Progress: {
      // Default (non-status) Progress stroke — AntD uses colorInfo here, which
      // is the dark slate `#314550` and reads as flat gray on dark surfaces.
      // Pin to the dark-mode cyan so the bar matches brand. `success` and
      // `exception` variants still derive from colorSuccess/colorError.
      defaultColor: BRAND_CYAN,
    },
  },
};

export type AppThemeMode = 'light' | 'dark';

/** Convenience map for selecting a theme by mode. */
export const themesByMode: Record<AppThemeMode, ThemeConfig> = {
  light: lightTheme,
  dark: darkTheme,
};
